import * as icons from '../../../../../assets/icons'
import { Box, Stack, Typography } from '@dtx-company/design-system/src'
import { EditTheme } from '../../../../../components/profile/Customize/types'
import { FC, useMemo } from 'react'
import {
  FONT_STYLESHEETS,
  FontFamilies,
  defaultUserTheme
} from '@dtx-company/true-common/src/constants/page'
import { FontStylePicker } from '../../../../../components/profile/Customize/FontStylePicker'
import { GoogleFontsPicker } from '../../../../../components/profile/Customize/GoogleFonts/GoogleFontsPicker'
import { GoogleFontsUpgradeTile } from '../../../../../components/profile/Customize/GoogleFonts/GoogleFontsUpgradeTile'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image'
import { PageType } from '@dtx-company/flow-codegen/src/page/typeUtils'
import { Select } from '@dtx-company/shared-components/src/components/atoms/Select'
import { SelectOption } from '@dtx-company/shared-components/src/types/atoms/options.types'
import { closeLinkEditDrawer } from '@dtx-company/inter-app/src/redux/slices/linkEditor/linkEditorSlice'
import { getLockedFieldFromTemplateConfig } from '../../../../FlowpageTemplates/utils/getLockedFieldFromTemplateConfig'
import { stopPropagationEvents } from '../LinkEditDrawer/LinkEditDrawer.utils'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useDispatch } from 'react-redux'
import { useEditThemeWithAutosave } from '../../../../../components/profile/Customize/hooks'
import { useGatedFlowpageFontOptions } from '../../../../../components/profile/Customize/FontFamilyPicker.hooks'
import { useLockedFeatureAction } from '@app/common/src/components/LockedFeatureModal/hooks/useLockedFeatureAction'
import { usePageTemplateEditorState } from '@dtx-company/inter-app/src/hooks/usePageTemplateEditorState'

import events from '@dtx-company/inter-app/src/event-tracking/events/flowpage'

export const FontMenu: FC<{ pageTheme: PageType['theme'] }> = ({ pageTheme }) => {
  const { availableFonts, gatedFonts, userIsViewer } = useGatedFlowpageFontOptions()
  const { hasOperation } = useAuthState()
  const dispatch = useDispatch()
  const { __typename, ...curTheme } = pageTheme || defaultUserTheme
  const editTheme = useEditThemeWithAutosave()
  const { isTemplateEditor } = usePageTemplateEditorState()

  const handleSelect = (option: string): void => {
    events.Flowpage_PageProfile_Selected_Font({
      font: option,
      deviceType: 'mobile'
    })
    editTheme({
      themeInput: {
        ...curTheme,
        fontFamily: option
      },
      themeId: curTheme.id
    })
  }
  const handleFontStyleChange = (theme: EditTheme): void => {
    editTheme({
      themeInput: {
        ...curTheme,
        ...theme
      },
      themeId: curTheme.id
    })
  }

  const availableFontOptions: SelectOption[] = useMemo(
    () => availableFonts.map(availFont => ({ value: availFont, label: availFont })),
    [availableFonts]
  )
  const gatedFontOptions: SelectOption[] = useMemo(
    () => gatedFonts.map(gatedFont => ({ value: gatedFont, label: gatedFont, icon: icons.lock })),
    [gatedFonts]
  )

  const openLockedFeatureModal = useLockedFeatureAction({
    lockedFeatureName: 'view:page_fonts'
  })

  const canUsePaidFonts = hasOperation('view:page_fonts')
  const showGoogleFontUpgradeTile = !canUsePaidFonts

  const disabled =
    !isTemplateEditor &&
    getLockedFieldFromTemplateConfig({
      templateConfig: pageTheme?.templateConfig,
      field: 'fontFamily'
    })
  return (
    <Stack sx={{ width: '100%' }} spacing={2}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant={{ xs: 'bodyMedium', lg: 'headlineXSmall' }}
          style={{ opacity: disabled ? 0.35 : 1 }}
        >
          {canUsePaidFonts ? 'Choose from Google Fonts' : 'Choose a page font'}
        </Typography>
      </Box>
      {canUsePaidFonts ? (
        <Box
          sx={{ minWidth: '100%', opacity: disabled ? 0.35 : 1, pr: 0.75 }}
          {...stopPropagationEvents}
        >
          <GoogleFontsPicker
            currFont={curTheme.fontFamily ?? defaultUserTheme.fontFamily}
            handleSelect={handleSelect}
            disabled={disabled}
          />
        </Box>
      ) : (
        // this condition may be deleted because we expose FP only to paid users
        <Box sx={{ opacity: disabled ? 0.35 : 1, width: '100%' }} {...stopPropagationEvents}>
          <Image
            height="44px"
            border="1px solid black"
            padding="4px"
            borderRadius="4px"
            marginBottom="16px"
            src={
              FONT_STYLESHEETS[(curTheme.fontFamily || defaultUserTheme.fontFamily) as FontFamilies]
                ?.iconSrc
            }
            alt="font icon"
          />
          <Select
            optionsMaxHeight={'200px'}
            disabled={userIsViewer || disabled}
            name="font-selector-mobile"
            value={{
              label: curTheme.fontFamily || defaultUserTheme.fontFamily,
              value: curTheme.fontFamily || defaultUserTheme.fontFamily
            }}
            options={availableFontOptions}
            disabledOptions={!hasOperation('view:page_fonts') ? gatedFontOptions : undefined}
            onChange={option => handleSelect(option.value)}
            onDisabledClick={() => {
              openLockedFeatureModal({})
              dispatch(closeLinkEditDrawer({}))
            }}
          />
        </Box>
      )}
      {showGoogleFontUpgradeTile && <GoogleFontsUpgradeTile />}
      <FontStylePicker
        handleChange={handleFontStyleChange}
        fontColor={curTheme.fontColor}
        titleAndDescriptionColor={curTheme.titleColor}
        textSize={curTheme.textSize}
        disabled={disabled}
      />
    </Stack>
  )
}
